import { useTranslation } from "react-i18next";
import { Calendar, Clock, Info, MoonStar, ReceiptText } from "lucide-react";
import { Alert, Button, Typography } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useOvernightConfig } from "@/hooks/useOvernightConfig";
import type { PanelPet } from "../panel/panelModel";
import { SectionBoundary } from "./SectionBoundary";
import { formatStayCurrency } from "./stayFormat";
import { useStayComanda } from "./useStayComanda";
import { useAddStayOvernight } from "./useStayOvernight";

export interface OvernightToggleSectionProps {
  pet: PanelPet;
  isoDate: string;
  /** Chamado após gravar, para o diálogo do card poder se fechar. */
  onAdded?: () => void;
}

/**
 * O conteúdo do pernoite. Vive em três lugares — botão do card, modal de
 * check-in e modal de acompanhar — e é sempre este componente.
 *
 * Pernoite é para quem passa o dia e vai dormir; reservas que já são hotel
 * (`nature === "hotel" | "both"`) já dormem no local, então a seção some para
 * elas mesmo que a clínica tenha o pernoite habilitado — o mesmo invariante
 * que `PetCard` aplica ao decidir mostrar o botão de lua.
 */
export function OvernightToggleSection({ pet, isoDate, onAdded }: OvernightToggleSectionProps) {
  const { i18n, t } = useTranslation("daycareHotel");
  const config = useOvernightConfig();
  const comanda = useStayComanda(pet.reservationId);
  const addOvernight = useAddStayOvernight();

  if (config.isLoading) return <SectionBoundary isLoading isError={false} skeletonClassName="h-32 rounded-2xl" />;
  if (!config.data?.isEnabled) return null;
  if (pet.nature === "hotel" || pet.nature === "both") return null;

  const language = i18n.resolvedLanguage ?? i18n.language ?? "pt-BR";
  const checkoutTime = config.data.checkoutTime ?? "12:00";

  if (pet.isOvernight) {
    return <Alert variant="info">{t("panel.overnightAlready")}</Alert>;
  }

  const confirm = async () => {
    try {
      await addOvernight.mutateAsync({
        reservationId: pet.reservationId,
        isoDate,
        checkoutTime,
        price: config.data?.price ?? null,
        saleId: comanda.data?.saleId ?? null,
        overnightItemName: t("badgeOvernight"),
      });
      toast({ title: t("panel.overnightAdded") });
      onAdded?.();
    } catch {
      toast({ variant: "destructive", title: t("panel.overnightAddFailed") });
    }
  };

  return (
    <section>
      <div className="mb-3 flex items-start gap-2.5 rounded-xl border border-akuna-purple/25 bg-purple-50 px-3.5 py-3">
        <Info aria-hidden="true" size={18} className="mt-0.5 shrink-0 text-akuna-purple" />
        <Typography variant="body-sm" className="text-purple-800">
          {t("panel.overnightExplainer", { time: checkoutTime })}
        </Typography>
      </div>

      <div className="mb-3 grid gap-3 sm:grid-cols-2">
        <div>
          <Typography variant="eyebrow" className="mb-1.5">{t("panel.overnightEntersToday")}</Typography>
          <div className="flex items-center gap-2 rounded-xl border border-border px-3 py-2.5">
            <Calendar aria-hidden="true" size={16} className="text-muted-foreground" />
            <Typography variant="body-sm">{isoDate}</Typography>
          </div>
        </div>
        <div>
          <Typography variant="eyebrow" className="mb-1.5">{t("panel.overnightLeavesTomorrow")}</Typography>
          <div className="flex items-center gap-2 rounded-xl border-[1.5px] border-akuna-purple bg-purple-50 px-3 py-2.5">
            <Clock aria-hidden="true" size={16} className="text-akuna-purple" />
            <Typography variant="body-sm" className="font-semibold text-purple-800">{checkoutTime}</Typography>
          </div>
        </div>
      </div>

      {config.data.price == null || config.data.price <= 0 ? (
        <Alert variant="warning">{t("panel.overnightNoPrice")}</Alert>
      ) : (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted px-3.5 py-3">
          <div className="flex items-center gap-2">
            <ReceiptText aria-hidden="true" size={18} className="text-akuna-orange" />
            <Typography variant="body-sm">{t("panel.overnightBilling")}</Typography>
          </div>
          <Typography variant="h5">{formatStayCurrency(config.data.price, language)}</Typography>
        </div>
      )}

      <Button
        className="mt-3 w-full"
        disabled={addOvernight.isPending}
        loading={addOvernight.isPending}
        onClick={confirm}
      >
        <MoonStar aria-hidden="true" size={18} /> {t("panel.overnightConfirm")}
      </Button>
    </section>
  );
}
